import { DropdownTrigger } from 'components/dropdown/DropdownTrigger';
import { NodeSettingsDisplayMode } from 'components/node_settings/shared';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

export const NodeSettingWrapper = styled.div`
    container-name: node-settings-wrapper;
    container-type: inline-size;
`;

export const NodeSettingsContainer = styled.div.attrs<{
    $displayMode: NodeSettingsDisplayMode;
}>((props) => ({
    className: props.className
        ? `${props.className} ${props.$displayMode}`
        : props.$displayMode,
}))`
    display: grid;
    grid-auto-rows: auto;
    gap: 2rem 4rem;
    justify-content: start;
    align-items: start;
    font-size: 1.4rem;
    max-width: 130rem;

    grid-template-columns: 1fr;

    @container node-settings-wrapper (min-width: 85rem) {
        grid-template-columns: 1fr 1fr;
    }
`;

export const NodeSettingsColumn = styled.div`
    display: block;
`;

export const SettingsList = styled.div<{ $minNameWidth?: number }>`
    display: grid;
    grid-template-columns: minmax(${({ $minNameWidth }) => ($minNameWidth ? `${$minNameWidth}px` : '160px')}, auto) 1fr;
`;

export const Description = styled.div`
    font-size: 1.2rem;
    color: ${tokens.text.secondary};
    grid-column: span 2;

    &:not(:last-child, :nth-last-child(2)) {
        border-bottom: 1px solid ${tokens.border.default};
        padding-bottom: 0.6rem;
    }
`;

export const SettingCell = styled.div<{
    $stacked?: boolean;
    $indented?: boolean;
    $hasDescription?: boolean;
}>`
    padding-top: 0.6rem;
    overflow-x: hidden;

    &:not(:last-child, :nth-last-child(2)) {
        border-bottom: 1px solid ${tokens.border.default};
        padding-bottom: 0.6rem;
    }

    ${({ $stacked }) => ($stacked ? 'grid-column: 1 / -1;' : '')}
    ${({ $hasDescription }) => ($hasDescription ? 'border-bottom: 0 !important;' : '')}
`;

export const SettingName = styled(SettingCell)`
    font-weight: 500;
    display: flex;
    align-items: center;
    padding-right: 1rem;

    ${({ $stacked }) => ($stacked ? 'padding-bottom: 0;' : '')}
    ${({ $indented }) => ($indented ? 'padding-left: 1.6rem;' : '')}
`;

export const SettingValue = styled(SettingCell)`
    ${({ $stacked }) => ($stacked ? 'padding-top: 1.2rem;' : '')}
`;

export const SettingsSection = styled.div<{ $order?: number }>`
    & + & {
        margin-top: 3rem;
    }

    border: 1px solid ${tokens.border.default};
    padding: 1.6rem;
    border-radius: 8px;

    ${({ $order }) => ($order !== undefined ? `order: ${$order}` : '')}
`;

export const BorderlessSettingsSection = styled.div<{ $order?: number }>`
    & + & {
        margin-top: 3rem;
    }

    ${({ $order }) => ($order !== undefined ? `order: ${$order}` : '')}
`;

export const SettingTitle = styled.div`
    font-size: 2rem;

    display: flex;
    justify-content: space-between;

    align-items: center;
    font-weight: 500;
`;

export const AcquisitionUnitSettingsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2rem;

    @media ${MediaQueries.largeAndUp} {
        & > :last-child {
            flex: 1 1 0;
            min-width: 0;
        }
    }

    @media ${MediaQueries.largeAndDown} {
        flex-direction: column;
    }
`;
export const PortTabRouterWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;
export const PortTabsHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;
export const PortTabsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    & > button,
    & > a {
        border-color: ${tokens.border.default};
        border-style: solid;
        border-width: 1px;
        // remove the double borders between tabs...
        &:not(:last-child) {
            border-right-width: 0;

            // ...but add them back on smaller screens where the tabs wrap, to maintain separation
            @media (width < 1374px) {
                border-right-width: 1px;
            }
        }
    }
    & > button[aria-current='false'],
    & > a[aria-current='false'] {
        border-bottom-width: 1px;
    }

    // ...add back on the border to the selected item, as we'll make its border
    // _way_ more prominent with color...
    & > button[aria-current],
    & > a[aria-current] {
        border-right-width: 1px;
    }

    // ...and hide the border of the element just after the selected one, to
    // remove any shift! (skipped at widths where tabs wrap, otherwise the
    // next-row first tab loses its left border.)
    @media (width >= 1374px) {
        & > button[aria-current] + *,
        & > a[aria-current] + * {
            border-left-width: 0px;
        }
    }
`;

export const OverviewSettingsContainer = styled.div`
    --inline-editor-button-alignment: flex-end;
    min-width: 38rem;

    @media ${MediaQueries.largeAndUp} {
        max-width: 38rem;
    }
`;

export const Separator = styled.div`
    border-right: 1px solid ${tokens.nav.border};

    @media ${MediaQueries.largeAndDown} {
        display: none;
    }
`;

export const MonitoringPointsContainer = styled.div`
    padding-top: 3rem;
`;

export const PortSettingsContainer = styled.div`
    display: grid;
    grid-auto-rows: auto;
    grid-template-columns: 1fr 1fr;

    width: 100%;
    gap: 2rem;
    overflow-y: auto;

    @media ${MediaQueries.xlargeAndDown} {
        grid-template-columns: 1fr;
    }
`;

export const PortSettingsColumn = styled(NodeSettingsColumn)``;

export const PortTabLink = styled(NavLink)<Readonly<{ padding?: string }>>`
    display: flex;
    box-sizing: border-box;
    height: 100%;
    align-items: center;
    font-size: 1.6rem;
    font-weight: normal;
    color: ${tokens.text.primary};
    text-decoration: none;
    border-bottom: 2px solid transparent;
    white-space: nowrap;

    &[aria-current] {
        border-color: ${tokens.primary.default};
        color: ${tokens.primary.default};
    }
`;

export const AcquisitionUnitModalSettingsContainer = styled.div`
    display: flex;
    flex-direction: row;
`;

export const CopyPortSettingsDropdownTrigger = styled(DropdownTrigger)`
    border-radius: 2px;
    border: 1px solid ${tokens.border.default};
    // subtract out that border
    padding: calc(0.8rem - 1px) calc(1rem - 1px);

    color: ${tokens.text.link};
`;

export const CopyPortSettingsContainer = styled.div`
    max-width: 50rem;
    display: flex;
    gap: 0.8rem;
    align-items: center;

    & > button {
        border-radius: 2px;
        padding: 0.8rem 1rem;
    }
`;

export const PortSettingsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1rem;
    padding-bottom: 1rem;
    margin-bottom: 2rem;
    border-bottom: 1px solid ${tokens.border.default};
    flex-wrap: wrap;
    gap: 1rem;
`;

export const SensorConfigTitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;
